import {
  AlchemyResource,
  APIRetry,
  Choice,
  ContentIdResource,
  ContentType,
  DiagnosticsActionStatus,
  GraphNode,
  ITextFieldOwnProps,
  JsonDocument,
  NodeType,
  ParamOrVar,
  RaisedEventState,
  StaticConfig,
  TextField
} from '../../models';
import { Error, IFormComponentProps } from '../../models/app.models';
import { DiagnosticsProgressResponse, FormV2, SafConstants } from '../../models/safDiagnostic.models';
import { YodaState } from '../../rootReducer';
import { createSelector, IImmutableList, IImmutableMap } from '../../state/redux';
import { IComponentStateProps } from '../../types';
import { embedVariableInText, wireupLinks } from '../../utils';

export const renderedNodesSelector = createSelector(
  [(state: YodaState) => state.get('IntentGraph').get('renderedNodes')],
  (renderedNodes: IImmutableList<IImmutableMap<GraphNode>>) => renderedNodes
);

const getNode = (renderedNodes: IImmutableList<IImmutableMap<GraphNode>>, props: IComponentStateProps) => {
  if (!!props.uId) {
    return renderedNodes.find((node: IImmutableMap<GraphNode>) => {
      return props.uId === node.get('uId');
    });
  }
};

export const getNodeSelector = createSelector(
  [renderedNodesSelector, (_state: YodaState, props: IComponentStateProps) => props],
  getNode
);

const getStaticChoices = (renderedNodes: IImmutableList<IImmutableMap<GraphNode>>, props: IComponentStateProps) => {
  const currentNode = getNode(renderedNodes, props);
  return !!currentNode && currentNode.get('staticChoices');
};

export const getStaticChoicesSelector = createSelector(
  [renderedNodesSelector, (_state: YodaState, props: IComponentStateProps) => props],
  getStaticChoices
);

const pauseEditForDiagnosticExecution = (
  renderedNodes: IImmutableList<IImmutableMap<GraphNode>>,
  props: IComponentStateProps
) => {
  let idx = -1;
  renderedNodes.find((node: IImmutableMap<GraphNode>, index: number) => {
    if (props.uId === node.get('uId')) {
      idx = index;
      return true;
    }
    return false;
  });

  let nextNode = null;
  let nextIsSAFNode = false;
  let inlineDiagnosticInProgress = false;
  if (idx !== -1) {
    nextNode = renderedNodes.get(idx + 1);
    if (!!nextNode) {
      nextIsSAFNode = nextNode.get('type').toLowerCase() === NodeType.SafDiagnosticNode.toLowerCase();
      inlineDiagnosticInProgress =
        nextNode.get('diagnosticsActionStatus') === DiagnosticsActionStatus.FetchingDiagnosticsInProgress;
    }
  }
  return !!nextIsSAFNode && inlineDiagnosticInProgress;
};

export const pauseForDiagnosticExecutionSelector = createSelector(
  [renderedNodesSelector, (_state: YodaState, props: IComponentStateProps) => props],
  pauseEditForDiagnosticExecution
);

const getIsInEditMode = (renderedNodes: IImmutableList<IImmutableMap<GraphNode>>, props: IComponentStateProps) => {
  const currentNode = getNode(renderedNodes, props);
  return !!currentNode && currentNode.get('isInEditMode');
};

export const getIsInEditModeSelector = createSelector(
  [renderedNodesSelector, (_state: YodaState, props: IComponentStateProps) => props],
  getIsInEditMode
);

const getDynamicChoicesList = (
  renderedNodes: IImmutableList<IImmutableMap<GraphNode>>,
  props: IComponentStateProps
) => {
  const currentNode = getNode(renderedNodes, props);
  return !!currentNode && currentNode.get('dynamicChoicesList');
};

export const getDynamicChoicesListSelector = createSelector(
  [renderedNodesSelector, (_state: YodaState, props: IComponentStateProps) => props],
  getDynamicChoicesList
);

const getDynamicChoices = (renderedNodes: IImmutableList<IImmutableMap<GraphNode>>, props: IComponentStateProps) => {
  const currentNode = getNode(renderedNodes, props);
  return !!currentNode && currentNode.get('dynamicChoices');
};

export const getDynamicChoicesSelector = createSelector(
  [renderedNodesSelector, (_state: YodaState, props: IComponentStateProps) => props],
  getDynamicChoices
);

const getSelectedChoice = (renderedNodes: IImmutableList<IImmutableMap<GraphNode>>, props: IComponentStateProps) => {
  const currentNode = getNode(renderedNodes, props);
  return !!currentNode && currentNode.get('selectedChoice');
};

export const getSelectedChoiceSelector = createSelector(
  [renderedNodesSelector, (_state: YodaState, props: IComponentStateProps) => props],
  getSelectedChoice
);

export const getJsonPayload = (jsonPayloads: IImmutableList<IImmutableMap<JsonDocument>>, dialogId: string) => {
  if (jsonPayloads && dialogId) {
    const jsonPayload = jsonPayloads.find((jsonPayload: IImmutableMap<JsonDocument>) => {
      return jsonPayload.get('meta').get('id') === dialogId;
    });
    return jsonPayload;
  }
  return null;
};

export const jsonPayloadsSelector = createSelector(
  [
    (state: YodaState) =>
      state
        .get('IntentGraph')
        .get('staticConfig')
        .get('jsonDocuments')
  ],
  (jsonPayloads: IImmutableList<IImmutableMap<JsonDocument>>) => jsonPayloads
);

export const jsonPayloadSelector = createSelector(
  [jsonPayloadsSelector, (_state: YodaState, props: IComponentStateProps) => props.dialogId],
  getJsonPayload
);

const getJsonPayloadMetadata = (jsonPayload: IImmutableMap<JsonDocument>) => {
  return !!jsonPayload && jsonPayload.get('meta');
};
export const jsonPayloadMetaSelector = createSelector([jsonPayloadSelector], getJsonPayloadMetadata);

export const topHeadingSelector = createSelector(
  [jsonPayloadSelector],
  (jsonPayload: IImmutableMap<JsonDocument>) => !!jsonPayload && jsonPayload.get('topHeadingText')
);

const getTextContent = (state: YodaState, props: IComponentStateProps) => {
  if (!!props.componentkey) {
    const keyProps = props.componentkey.split('$');
    let uId: string;
    let property: string;
    let index: number;
    if (keyProps[0] === 'document' && keyProps.length >= 2) {
      return topHeadingSelector(state, props);
    } else if (keyProps.length >= 2) {
      uId = keyProps[0];
      property = keyProps[1];
      if (
        keyProps.length > 2 &&
        (property.toLowerCase() === 'staticChoices' || property.toLowerCase() === 'dynamicChoicesList')
      ) {
        index = Number(keyProps[2]);
      }
      const node: IImmutableMap<GraphNode> = state
        .get('IntentGraph')
        .get('renderedNodes')
        .find((n: IImmutableMap<GraphNode>) => {
          return n.get('uId') === uId;
        });

      switch (property) {
        case 'staticChoices':
        case 'dynamicChoicesList':
          return node
            .get(property)
            .get(index)
            .get('text');
        case 'question':
        case 'title':
        case 'text':
          return node.get(property);
      }
    }
  }
};
export const textContentSelector = createSelector([getTextContent], (t: IImmutableMap<TextField>) => t);

export const staticConfigSelector = createSelector(
  [(state: YodaState) => state.get('IntentGraph').get('staticConfig')],
  (staticConfig: IImmutableMap<StaticConfig>) => staticConfig
);

export const enableScrollToBottomSelector = createSelector(
  [staticConfigSelector],
  (staticConfig: IImmutableMap<StaticConfig>) => staticConfig.get('enableScrollToBottom')
);

export const enableScrollToTopSelector = createSelector(
  [staticConfigSelector],
  (staticConfig: IImmutableMap<StaticConfig>) => staticConfig.get('enableScrollToTop')
);

export const diagnosticsMaxRetriesSelector = createSelector(
  [staticConfigSelector],
  (staticConfig: IImmutableMap<StaticConfig>) =>
    staticConfig.get('safDiagnosticsSettings') && staticConfig.get('safDiagnosticsSettings').get('safActionMaxRetries')
      ? staticConfig.get('safDiagnosticsSettings').get('safActionMaxRetries')
      : SafConstants.SAFDiagnosticsMaxRetriesCount
);

export const scrollRefClassNameSelector = createSelector(
  [staticConfigSelector],
  (staticConfig: IImmutableMap<StaticConfig>) => staticConfig.get('scrollRef')
);

export const sessionIdSelector = createSelector([staticConfigSelector], (staticConfig: IImmutableMap<StaticConfig>) =>
  staticConfig.get('sessionId')
);

export const correlationIdSelector = createSelector(
  [staticConfigSelector],
  (staticConfig: IImmutableMap<StaticConfig>) => staticConfig.get('correlationId')
);

export const isComponentInitializedSelector = createSelector(
  [(state: YodaState) => state.get('IntentGraph').get('isComponentInitialized')],
  (i: boolean) => i
);

export const isDiagnosticActionInProgressSelector = createSelector(
  [(state: YodaState) => state.get('IntentGraph').get('isDiagnosticsActionInProgress')],
  (i: boolean) => !!i
);

export const isFetchingDialogInProgressSelector = createSelector(
  [(state: YodaState) => state.get('IntentGraph').get('isFetchingDialog')],
  (i: boolean) => i
);

const errorMapSelector = createSelector(
  (state: YodaState) => state.get('IntentGraph').get('errorMap'),
  (errorMap: IImmutableList<IImmutableMap<Error>>) => errorMap
);

const getErrorByComponentKey = (errorMap: IImmutableList<IImmutableMap<Error>>, props: IFormComponentProps) => {
  if (errorMap && errorMap.size) {
    const componentKey =
      props.componentModel && props.componentModel.get('Key') ? props.componentModel.get('Key') : props.componentKey;
    return componentKey
      ? errorMap.find((error: IImmutableMap<Error>) => error.get('ComponentKey') === componentKey)
      : null;
  }
  return null;
};

export const errorSelector = createSelector(
  [errorMapSelector, (_state: YodaState, props: IFormComponentProps) => props],
  getErrorByComponentKey
);

export const diagnosticsResponseSelector = createSelector(
  [(state: YodaState) => state.get('IntentGraph').get('diagnosticsResponse')],
  (data: IImmutableMap<DiagnosticsProgressResponse>) => data
);

export const diagnosticsFormSelector = createSelector(
  (state: YodaState) => state.get('IntentGraph').get('diagnosticsFormModel'),
  (diagForm: IImmutableMap<FormV2>) => diagForm
);

export const diagnosticsProgressSelectror = createSelector(
  [(state: YodaState) => state.get('IntentGraph').get('diagnosticsProgress')],
  (data: number) => data
);

export const raisedEventsSelector = createSelector(
  [(state: YodaState) => state.get('IntentGraph').get('raisedEvents')],
  (data: IImmutableList<IImmutableMap<RaisedEventState>>) => data
);

const getNodeRaisedEvents = (
  raisedEvents: IImmutableList<IImmutableMap<RaisedEventState>>,
  props: IComponentStateProps
) => {
  let raisedEvent;
  if (raisedEvents && raisedEvents.size) {
    const uId = props.uId;
    raisedEvent = raisedEvents.find((r: IImmutableMap<RaisedEventState>) => {
      return r && uId && r.get('raisedBy') === uId;
    });
  }
  return raisedEvent || null;
};

export const nodeRaisedEventsSelector = createSelector(
  [raisedEventsSelector, (_state: YodaState, props: IComponentStateProps) => props],
  getNodeRaisedEvents
);

export const globalVarsSelector = createSelector(
  (state: YodaState) => state.get('IntentGraph').get('globalVars'),
  (globalVars: IImmutableList<IImmutableMap<ParamOrVar>>) => globalVars
);

export const enableLinkClickLoggingSelector = createSelector(
  [staticConfigSelector],
  (staticConfig: IImmutableMap<StaticConfig>) => staticConfig.get('logLinkClicks')
);

export const enableUsingCallBacksForSAFApisSelector = createSelector(
  [staticConfigSelector],
  (staticConfig: IImmutableMap<StaticConfig>) => staticConfig.get('useCallBacksForSAFApis')
);

export const apiRetriesSelector = createSelector(
  [(state: YodaState) => state.get('IntentGraph').get('apiRetries')],
  (apiRetries: IImmutableList<IImmutableMap<APIRetry>>) => apiRetries
);

export const resourcesSelector = createSelector(
  [(state: YodaState) => state.get('IntentGraph').get('resources')],
  (resources: IImmutableList<IImmutableMap<AlchemyResource>>) => resources
);

export const getResource = (
  resources: IImmutableList<IImmutableMap<AlchemyResource>>,
  globalVars: IImmutableList<IImmutableMap<ParamOrVar>>,
  textContent: IImmutableMap<TextField>
) => {
  let solutionContent = '';
  let solutionTitle = '';
  let isFetched = false;
  if (textContent) {
    const contentType = textContent.get('contentType');
    const isDynamicContent = textContent.get('dynamic');
    if (contentType) {
      switch (contentType.toLowerCase()) {
        case ContentType.AlchemyResource.toLowerCase():
        case ContentType.Dmc.toLowerCase():
        case ContentType.Soc.toLowerCase():
          if (resources && resources.size > 0) {
            const resource = resources.find(
              (r: IImmutableMap<AlchemyResource>) => r.get('contentId') === textContent.get('content')
            );
            if (resource) {
              solutionContent = resource.get('solutionContent');
              solutionTitle = resource.get('solutionTitle');
              isFetched = true;
            }
          }
          break;
        default:
          solutionContent = textContent.get('content');
          solutionTitle = textContent.get('contentTitle');
          break;
      }
      if(contentType !== ContentType.EmbeddedPlainText){
        solutionContent = wireupLinks(solutionContent);
      }
    } else {
      solutionContent = textContent.get('content');
      solutionTitle = textContent.get('contentTitle');
    }

    if (isDynamicContent) {
      solutionContent = embedVariableInText(solutionContent, globalVars);
      solutionTitle = embedVariableInText(solutionTitle, globalVars);
    }
  }

  return {
    SolutionContent: solutionContent,
    SolutionTitle: solutionTitle,
    IsFetched: isFetched
  };
};

export const resourceSelector = createSelector(
  [resourcesSelector, globalVarsSelector, (_state: YodaState, props: ITextFieldOwnProps) => props.textContent],
  getResource
);

const getIsFetchingResource = (
  resources: IImmutableList<IImmutableMap<AlchemyResource>>,
  props: ITextFieldOwnProps
) => {
  if (props.textContent) {
    const contentType = props.textContent.get('contentType');
    if (contentType && resources && resources.size > 0) {
      switch (contentType.toLowerCase()) {
        case ContentType.AlchemyResource.toLowerCase():
        case ContentType.Dmc.toLowerCase():
        case ContentType.Soc.toLowerCase():
          const resource = resources.find(
            (r: IImmutableMap<AlchemyResource>) => r.get('contentId') === props.textContent.get('content')
          );
          return !!resource && resource.get('isLoading');
        default:
          break;
      }
    }
  }

  return false;
};

export const isFetchingResourceSelector = createSelector(
  [resourcesSelector, (_state: YodaState, props: ITextFieldOwnProps) => props],
  getIsFetchingResource
);

const extractContentId = (textFields: IImmutableMap<TextField>[], contentIds: ContentIdResource[]) => {
  if (textFields) {
    textFields.forEach((textField: IImmutableMap<TextField>) => {
      if (textField && textField.get('contentType')) {
        switch (textField.get('contentType').toLowerCase()) {
          case ContentType.AlchemyResource.toLowerCase():
          case ContentType.Dmc.toLowerCase():
          case ContentType.Soc.toLowerCase():
            const contentResource: ContentIdResource = {
              contentId: textField.get('content'),
              isDynamic: !!textField.get('dynamic'),
              contentType: textField.get('contentType')
            };
            contentIds.push(contentResource);
            break;
          default:
            break;
        }
      }
    });
  }
};

const getContentIdFromChoices = (node: IImmutableMap<GraphNode>, contentIds: ContentIdResource[]) => {
  if (!!node) {
    const staticChoices = node.get('staticChoices');
    if (staticChoices) {
      staticChoices.forEach((choice: IImmutableMap<Choice>) => {
        if (choice) {
          extractContentId([choice.get('text')], contentIds);
        }
      });
    }
    const dynamicChoices = node.get('dynamicChoicesList');
    if (dynamicChoices) {
      dynamicChoices.forEach((choice: IImmutableMap<Choice>) => {
        if (choice) {
          extractContentId([choice.get('text')], contentIds);
        }
      });
    }
  }
};

const getContentIds = (node: IImmutableMap<GraphNode>) => {
  const contentIds: ContentIdResource[] = [];
  if (!!node) {
    let textFields: IImmutableMap<TextField>[] = [node.get('title'), node.get('text')];
    if (node.get('preCondition')) {
      textFields.push(node.get('preCondition').get('text'));
    }
    switch (node.get('type').toLowerCase()) {
      case NodeType.ChoiceNode.toLowerCase():
        textFields.push(node.get('question'));
        getContentIdFromChoices(node, contentIds);
        break;
      case NodeType.InputNode.toLowerCase():
        textFields = textFields.concat([node.get('tooltip'), node.get('placeholder'), node.get('buttonText')]);
        break;
      default:
        break;
    }
    extractContentId(textFields, contentIds);
  }
  return contentIds;
};

export const contentIdsSelector = createSelector([getNodeSelector], getContentIds);

const getIsFetchingResources = (
  resources: IImmutableList<IImmutableMap<AlchemyResource>>,
  contentIds: ContentIdResource[]
) => {
  let isFetchingResources = false;
  if (!!contentIds && resources && resources.size > 0) {
    if (contentIds.length > 0) {
      contentIds.forEach((contentResource: ContentIdResource) => {
        const resource = resources.find(
          (r: IImmutableMap<AlchemyResource>) => r.get('contentId') === contentResource.contentId
        );
        if (!resource || (resource && resource.get('isLoading'))) {
          isFetchingResources = true;
          return;
        }
      });
    }
    return isFetchingResources;
  }
};

export const isFetchingResourcesSelector = createSelector(
  [resourcesSelector, contentIdsSelector],
  getIsFetchingResources
);

const getButtonText = (resources: IImmutableList<IImmutableMap<AlchemyResource>>, node: IImmutableMap<GraphNode>, globalVars: IImmutableList<IImmutableMap<ParamOrVar>>,
  ) => {
  let buttonText = 'Confirm';
  if(node && node.get('buttonText')){
    const resource = getResource(resources, globalVars, node.get('buttonText'));
    if(resource){
      buttonText = resource.SolutionContent || buttonText;
    }
  }
  return buttonText;
};

export const buttonTextSelector = createSelector([resourcesSelector, getNodeSelector, globalVarsSelector], getButtonText);

export const skipRenderingTextFieldSelector = createSelector( getNodeSelector, (n: IImmutableMap<GraphNode>) => n.get('type').toLowerCase() === NodeType.PartnerSolutionNode);